import { useFeaturedArticle } from "@hooks/useFeaturedArticle";
import { useLocalisation } from "@hooks/useLocalisation";
import { type ComponentProps, type FromSanityGraphQL } from "@ts/components";
import { type Article, type SectionFeaturedArticle } from "@ts/sanity";
import React, { type FC } from "react";

export type FeaturedArticleInputProps = ComponentProps &
  FromSanityGraphQL<SectionFeaturedArticle>;

export type FeaturedArticleOutputProps = Omit<
  FeaturedArticleInputProps,
  "articlesCategories"
> & {
  featuredArticles?: Article[];
};

const filterArticleByHiddenSettings = (
  articles: { node: Article }[],
  currentLocale: string,
): Article[] => {
  const regionVisibleArticles = articles?.filter(
    (article) =>
      !article?.node?.hiddenInWebsites?.some((website) =>
        website.includes(currentLocale),
      ),
  );
  return regionVisibleArticles.map(({ node }) => node);
};

export const withFeaturedArticle =
  (Component: FC<FeaturedArticleOutputProps>) =>
  ({
    name = "FeaturedArticle",
    articlesSource,
    articlesCategories,
    initialAmountToShow,
    analytics,
    ...rest
  }: FeaturedArticleInputProps) => {
    const { currentLocale } = useLocalisation();
    const featuredArticleCategories = articlesCategories?.map(
      ({ value }) => value,
    );
    const { featuredArticles: filteredArticles } = useFeaturedArticle(
      featuredArticleCategories,
    );
    const filteredArticlesByHiddenSettings = filterArticleByHiddenSettings(
      filteredArticles,
      currentLocale?.languageCode || "en-US",
    );
    const featuredArticles = filteredArticlesByHiddenSettings?.splice(
      0,
      initialAmountToShow,
    );

    Component.displayName = name;

    if (!articlesSource) {
      return null;
    }

    return (
      <Component
        {...rest}
        articlesSource={articlesSource}
        featuredArticles={featuredArticles}
      />
    );
  };
